<template>
  <div class="comic-detail">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>正在加载漫画详情...</p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-state">
      <div class="error-container">
        <svg class="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 10h.01"/><path d="M15 10h.01"/><path d="M9.5 15.5a4 4 0 0 1 5 0"/></svg>
        <h3>加载失败</h3>
        <p>{{ error }}</p>
        <button @click="fetchComicDetail" class="retry-btn">
          <svg class="retry-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/></svg>
          重新加载
        </button>
      </div>
    </div>

    <!-- 正常内容 -->
    <template v-else>
      <!-- 背景模糊图片 -->
      <div class="background-blur" :style="{ backgroundImage: `url(${comic.cover})` }"></div>

      <!-- 主要内容区域 -->
      <div class="content-wrapper">
        <!-- 漫画头部信息卡片 -->
        <div class="comic-header-card">
          <div class="cover-container">
            <img class="cover" :src="comic.cover" alt="漫画封面" />
            <div class="cover-overlay">
              <button
                class="quick-read-btn"
                @click="readChapter(comic.chapters[0]?.id)"
                :disabled="!comic.chapters.length"
              >
                <span class="play-icon">▶</span>
                {{ comic.chapters.length ? '开始阅读' : '暂无章节' }}
              </button>
            </div>
          </div>

          <div class="comic-info">
            <div class="title-section">
              <h1 class="comic-title">{{ comic.title }}</h1>
              <div class="comic-tags">
                <span class="tag category">{{ comic.category_name }}</span>
                <span class="tag status" :class="comic.status_text === '连载中' ? 'ongoing' : 'completed'">
                  {{ comic.status_text }}
                </span>
                <span class="tag price" :class="comic.is_free ? 'free' : 'paid'">
                  {{ comic.is_free ? '免费' : '付费' }}
                </span>
              </div>
            </div>

            <div class="author-section">
              <span class="author-label">作者</span>
              <span class="author-name">{{ comic.author }}</span>
            </div>

            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-number">{{ comic.chapter_count }}</div>
                <div class="stat-label">总章节</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ formatNumber(comic.views) }}</div>
                <div class="stat-label">浏览量</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ formatNumber(comic.likes) }}</div>
                <div class="stat-label">点赞数</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">{{ freeChaptersCount }}</div>
                <div class="stat-label">免费章节</div>
              </div>
            </div>

            <div class="action-section">
              <button
                class="primary-read-btn"
                @click="readChapter(comic.chapters[0]?.id)"
                :disabled="!comic.chapters.length"
              >
                <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                立即阅读
              </button>
              <button
                class="favorite-btn"
                @click="toggleFavorite"
                :disabled="favoriteLoading || !isLoggedIn"
                :class="{ 'is-favorite': isFavorite }"
              >
                <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                {{ isFavorite ? '已收藏' : '收藏' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 简介卡片 -->
        <div class="intro-card">
          <div class="card-header">
            <h3 class="card-title">
              <svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              作品简介
            </h3>
          </div>
          <div class="card-content">
            <p class="description">{{ comic.description || '暂无简介' }}</p>
          </div>
        </div>

        <!-- 版权信息卡片 -->
        <div class="copyright-card">
          <div class="card-header">
            <h3 class="card-title">
              <span class="title-icon">©️</span>
              版权信息
            </h3>
          </div>
          <div class="card-content">
            <div class="copyright-grid">
              <div class="copyright-item">
                <span class="copyright-label">作者/创作者</span>
                <span class="copyright-value">{{ comic.author || '待完善' }}</span>
              </div>
              <div class="copyright-item">
                <span class="copyright-label">出品方/版权方</span>
                <span class="copyright-value">北京格瑞创新科技有限公司</span>
              </div>
              <div class="copyright-item">
                <span class="copyright-label">作品状态</span>
                <span class="copyright-value">{{ comic.status_text || '连载中' }}</span>
              </div>
              <div class="copyright-item">
                <span class="copyright-label">上线时间</span>
                <span class="copyright-value">{{ comic.publish_time || '待更新' }}</span>
              </div>
            </div>
            <div class="copyright-statement">
              <span class="statement-icon">©️</span>
              <span>本作品版权归作者及平台所有，未经授权禁止转载、摘编或利用其他方式使用。侵权必究。</span>
            </div>
          </div>
        </div>

        <!-- 章节列表卡片 -->
        <div class="chapters-card">
          <div class="card-header">
            <h3 class="card-title">
              <svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
              章节列表
              <span class="chapter-count">({{ comic.chapters.length }}章)</span>
            </h3>
            <div class="chapter-controls">
              <button class="sort-btn" @click="toggleSortOrder">
                <span class="sort-icon">{{ sortAsc ? '↑' : '↓' }}</span>
                {{ sortAsc ? '正序' : '倒序' }}
              </button>
            </div>
          </div>
          <div class="card-content">
            <div v-if="!comic.chapters.length" class="empty-chapters">
              <svg class="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <p>暂无章节</p>
            </div>
            <div v-else class="chapters-grid">
              <div
                v-for="(chapter, index) in sortedChapters"
                :key="chapter.id"
                class="chapter-item"
                @click="readChapter(chapter.id)"
              >
                <div class="chapter-number">{{ index + 1 }}</div>
                <div class="chapter-info">
                  <div class="chapter-title">{{ chapter.title }}</div>
                  <div class="chapter-meta">
                    <span class="chapter-price" :class="chapter.is_free ? 'free' : 'paid'">
                      {{ chapter.is_free ? '免费' : '¥' + chapter.price }}
                    </span>
                  </div>
                </div>
                <div class="chapter-arrow">→</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 评论区域卡片 -->
        <div class="comments-card">
          <div class="card-header">
            <h3 class="card-title">
              <svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              评论区
              <span class="comment-count">({{ commentStats.totalComments }}条评论)</span>
            </h3>
            <div class="comment-stats">
              <span class="rating-display">
                <span class="stars">
                  <svg v-for="i in 5" :key="i" class="star"
                       :class="{ 'filled': i <= Math.round(commentStats.averageRating) }"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </span>
                <span class="rating-text">{{ commentStats.averageRating.toFixed(1) }}</span>
              </span>
            </div>
          </div>

          <div class="card-content">
            <!-- 评论表单 -->
            <div class="comment-form-section">
              <div v-if="!isLoggedIn" class="login-prompt">
                <p>请先<router-link to="/profile">登录</router-link>后发表评论</p>
              </div>
              <div v-else>
                <button v-if="!showCommentForm" @click="showCommentForm = true" class="add-comment-btn">
                  <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
                  写评论
                </button>
                <div v-else class="comment-form">
                  <div class="form-header">
                    <h4>发表评论</h4>
                    <button @click="cancelComment" class="form-close-btn">×</button>
                  </div>
                  <div class="rating-input">
                    <label>评分：</label>
                    <div class="star-rating">
                      <svg v-for="i in 5" :key="i"
                           class="star-input"
                           :class="{ 'active': i <= newComment.rating }"
                           @click="newComment.rating = i"
                           viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    </div>
                  </div>
                  <textarea
                    v-model="newComment.content"
                    placeholder="分享你对这部漫画的看法..."
                    class="comment-textarea"
                    rows="4"
                    maxlength="500"
                  ></textarea>
                  <div class="form-footer">
                    <span class="char-count">{{ newComment.content.length }}/500</span>
                    <div class="form-buttons">
                      <button @click="cancelComment" class="cancel-btn">取消</button>
                      <button @click="submitComment" class="submit-btn"
                              :disabled="!newComment.content.trim()">发布</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 评论列表 -->
            <div class="comments-list">
              <div v-if="comments.length === 0" class="empty-comments">
                <svg class="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <p>还没有评论，快来抢沙发吧！</p>
              </div>
              <div v-else>
                <div v-for="comment in comments" :key="comment.id" class="comment-item">
                  <div class="comment-header">
                    <div class="user-info">
                      <span class="username">{{ comment.username }}</span>
                      <div class="comment-rating">
                        <svg v-for="i in 5" :key="i" class="star"
                             :class="{ 'filled': i <= comment.rating }"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </div>
                    </div>
                    <div class="comment-meta">
                      <span class="comment-time">{{ formatTime(comment.createtime) }}</span>
                      <button v-if="canDeleteComment(comment)"
                              @click="deleteComment(comment.id)"
                              class="delete-btn">删除</button>
                    </div>
                  </div>
                  <div class="comment-content">
                    <p>{{ comment.content }}</p>
                  </div>
                  <div class="comment-actions">
                    <button @click="likeComment(comment.id)" class="like-btn">
                      <svg class="like-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                      <span class="like-count">{{ comment.likes || 0 }}</span>
                    </button>
                    <button @click="startReply(comment.id)" class="reply-btn">
                      <svg class="reply-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      回复
                    </button>
                  </div>

                  <!-- 回复表单 -->
                  <div v-if="replyingTo === comment.id" class="reply-form">
                    <textarea
                      v-model="replyContent"
                      placeholder="写下你的回复..."
                      class="reply-textarea"
                      rows="2"
                      maxlength="200"
                    ></textarea>
                    <div class="reply-form-footer">
                      <span class="char-count">{{ replyContent.length }}/200</span>
                      <div class="reply-buttons">
                        <button @click="cancelReply" class="cancel-btn">取消</button>
                        <button @click="submitReply(comment.id)" class="submit-btn"
                                :disabled="!replyContent.trim()">回复</button>
                      </div>
                    </div>
                  </div>

                  <!-- 回复列表 -->
                  <div v-if="comment.replies && comment.replies.length > 0" class="replies-list">
                    <div v-for="reply in comment.replies" :key="reply.id" class="reply-item">
                      <div class="reply-header">
                        <span class="reply-username">{{ reply.username }}</span>
                        <span class="reply-time">{{ formatTime(reply.createtime) }}</span>
                      </div>
                      <div class="reply-content">
                        <p>{{ reply.content }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getComicDetail, BASE_URL, getFavoriteStatus, toggleFavorite, getComments, addComment, addReply, likeCommentApi, deleteCommentApi } from '../api/index';
import youthModeManager from '@/utils/youthMode';

export default {
  name: 'ComicDetail',
  data() {
    return {
      loading: false,
      error: null,
      sortAsc: true,
      comic: {
        id: 0,
        title: '',
        author: '',
        cover: '',
        description: '',
        category_name: '',
        status_text: '',
        chapter_count: 0,
        free_chapters_text: '',
        is_free: 0,
        price: '0.00',
        views: 0,
        likes: 0,
        publish_time: '',
        chapters: []
      },
      // 评论相关数据
      comments: [],
      commentStats: {
        totalComments: 0,
        totalReplies: 0,
        averageRating: 0,
        totalInteractions: 0
      },
      newComment: {
        content: '',
        rating: 5
      },
      showCommentForm: false,
      isLoggedIn: false,
      currentUsername: '',
      replyingTo: null,
      replyContent: '',
      isFavorite: false,
      favoriteLoading: false
    }
  },
  computed: {
    sortedChapters() {
      const chapters = [...this.comic.chapters];
      return this.sortAsc ? chapters : chapters.reverse();
    },
    // 根据章节列表实际统计免费章节数，避免与后端 free_chapters 字段不一致
    freeChaptersCount() {
      if (!this.comic.chapters || !this.comic.chapters.length) return 0;
      const freeCount = this.comic.chapters.filter(ch => ch.is_free == 1).length;
      if (freeCount === this.comic.chapters.length) {
        return '全部';
      }
      return freeCount;
    }
  },
  watch: {
    '$route'() {
      // 路由变化时重新加载数据
      this.fetchComicDetail();
      this.checkLoginStatus();
      this.loadComments();
    }
  },
  created() {
    this.fetchComicDetail();
    this.checkLoginStatus();
    this.loadFavoriteStatus();
    this.loadComments();
  },
  methods: {
    async fetchComicDetail() {
      try {
        this.loading = true;
        this.error = null;

        // 未成年人保护：青少年模式开启时仅允许访问适龄内容池
        await youthModeManager.ensureReady();
        const comicId = this.$route.params.id;
        if (!youthModeManager.isComicAllowed(comicId)) {
          youthModeManager.notifyContentBlocked();
          this.$router.replace('/');
          return;
        }

        if (!comicId) {
          throw new Error('漫画ID不能为空');
        }

        const res = await getComicDetail(comicId);
        if (res.data && res.data.code === 1) {
          const data = res.data.data;
          this.comic = {
            ...data.comic,
            cover: data.comic.cover.startsWith('http') ? data.comic.cover : BASE_URL + data.comic.cover,
            chapters: data.chapters || []
          };
        } else {
          throw new Error(res.data?.msg || '获取漫画详情失败');
        }
      } catch (e) {
        console.error('获取漫画详情失败:', e);
        this.error = e.message || '加载失败，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    readChapter(chapterId) {
      if (chapterId) {
        console.log('[ComicDetail] 开始跳转到阅读器页面，章节ID:', chapterId);
        console.log('[ComicDetail] 当前漫画信息:', JSON.stringify(this.comic));

        // 找到当前选中的章节信息
        const selectedChapter = this.comic.chapters.find(chapter => chapter.id === chapterId);
        console.log('[ComicDetail] 选中章节信息:', selectedChapter);

        // 记录跳转前的状态
        console.log('[ComicDetail] 跳转前状态 - 当前路由:', this.$route.path);

        this.$router.push({
          name: 'ComicReader',
          params: {
            id: chapterId,
            comicId: this.comic.id // 传递漫画ID
          }
        });
        console.log('[ComicDetail] 路由跳转请求已发送，包含漫画ID:', this.comic.id);
      } else {
        console.error('[ComicDetail] 跳转失败：无效的章节ID');
      }
    },

    // 格式化数字显示
    formatNumber(num) {
      if (num >= 10000) {
        return (num / 10000).toFixed(1) + '万';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
      }
      return num.toString();
    },

    // 切换排序顺序
    toggleSortOrder() {
      this.sortAsc = !this.sortAsc;
    },

    // ========== 评论相关方法 ==========

    // 检查登录状态
    checkLoginStatus() {
      const token = localStorage.getItem('userToken');
      this.isLoggedIn = !!token;
      this.currentUsername = localStorage.getItem('username') || '';
    },

    // 加载收藏状态
    async loadFavoriteStatus() {
      if (!this.isLoggedIn) return;
      try {
        const comicId = this.$route.params.id;
        const res = await getFavoriteStatus(comicId);
        if (res.data && res.data.code === 1) {
          this.isFavorite = !!res.data.data.is_favorite;
        }
      } catch (e) {
        console.error('获取收藏状态失败:', e);
      }
    },

    // 切换收藏
    async toggleFavorite() {
      if (!this.isLoggedIn) {
        alert('请先登录');
        return;
      }
      if (this.favoriteLoading) return;

      this.favoriteLoading = true;
      try {
        const comicId = this.$route.params.id;
        const res = await toggleFavorite(comicId);
        if (res.data && res.data.code === 1) {
          this.isFavorite = !!res.data.data.is_favorite;
        } else {
          alert(res.data?.msg || '操作失败');
        }
      } catch (e) {
        console.error('收藏操作失败:', e);
        alert('操作失败');
      } finally {
        this.favoriteLoading = false;
      }
    },

    // 加载评论
    async loadComments() {
      const comicId = this.$route.params.id;
      if (!comicId) return;

      try {
        const res = await getComments(comicId, { page: 1, limit: 50 });
        if (res.data && res.data.code === 1) {
          this.comments = res.data.data.list || [];
          this.computeCommentStats();
          console.log('[ComicDetail] 评论加载完成:', this.comments.length, '条评论');
        }
      } catch (e) {
        console.error('加载评论失败:', e);
      }
    },

    // 计算评论统计
    computeCommentStats() {
      const totalComments = this.comments.length;
      const totalReplies = this.comments.reduce((sum, c) => sum + (c.replies?.length || 0), 0);
      const averageRating = totalComments > 0
        ? this.comments.reduce((sum, c) => sum + (c.rating || 5), 0) / totalComments
        : 0;
      this.commentStats = {
        totalComments,
        totalReplies,
        averageRating,
        totalInteractions: totalComments + totalReplies
      };
    },

    // 提交评论
    async submitComment() {
      try {
        if (!this.isLoggedIn) {
          alert('请先登录后再发表评论');
          return;
        }

        const comicId = this.$route.params.id;
        const content = this.newComment.content.trim();
        const rating = this.newComment.rating;

        if (!content) {
          alert('请输入评论内容');
          return;
        }

        const res = await addComment({
          comic_id: comicId,
          content: content,
          rating: rating
        });

        if (res.data && res.data.code === 1) {
          this.newComment.content = '';
          this.newComment.rating = 5;
          this.showCommentForm = false;
          await this.loadComments();
        } else {
          alert(res.data?.msg || '发布失败');
        }
      } catch (error) {
        console.error('[ComicDetail] 发布评论失败:', error);
        alert(error.message || '发布评论失败，请重试');
      }
    },

    // 取消评论
    cancelComment() {
      this.newComment.content = '';
      this.newComment.rating = 5;
      this.showCommentForm = false;
    },

    // 删除评论
    async deleteComment(commentId) {
      if (!confirm('确定要删除这条评论吗？')) return;

      try {
        const res = await deleteCommentApi(commentId);
        if (res.data && res.data.code === 1) {
          await this.loadComments();
        } else {
          alert(res.data?.msg || '删除失败');
        }
      } catch (error) {
        console.error('删除评论失败:', error);
        alert('删除失败');
      }
    },

    // 点赞评论
    async likeComment(commentId) {
      try {
        const res = await likeCommentApi(commentId);
        if (res.data && res.data.code === 1) {
          const comment = this.findCommentRecursive(this.comments, commentId);
          if (comment) {
            comment.likes = res.data.data.likes || (comment.likes + 1);
          }
        }
      } catch (error) {
        console.error('点赞失败:', error);
      }
    },

    // 递归查找评论
    findCommentRecursive(comments, commentId) {
      for (const c of comments) {
        if (c.id === commentId) return c;
        if (c.replies && c.replies.length) {
          const found = this.findCommentRecursive(c.replies, commentId);
          if (found) return found;
        }
      }
      return null;
    },

    // 开始回复
    startReply(commentId) {
      this.replyingTo = commentId;
      this.replyContent = '';
    },

    // 取消回复
    cancelReply() {
      this.replyingTo = null;
      this.replyContent = '';
    },

    // 提交回复
    async submitReply(commentId) {
      try {
        const content = this.replyContent.trim();

        if (!content) {
          alert('请输入回复内容');
          return;
        }

        const res = await addReply(commentId, content);
        if (res.data && res.data.code === 1) {
          this.cancelReply();
          await this.loadComments();
        } else {
          alert(res.data?.msg || '回复失败');
        }
      } catch (error) {
        console.error('[ComicDetail] 发布回复失败:', error);
        alert(error.message || '发布回复失败，请重试');
      }
    },

    // 格式化时间（兼容秒级/毫秒级时间戳）
    formatTime(timestamp) {
      if (!timestamp) return '';
      // 后端返回的是 Unix 秒级时间戳，前端 Date 需要毫秒
      const ts = String(timestamp).length <= 10 ? timestamp * 1000 : timestamp;
      const date = new Date(ts);
      if (isNaN(date.getTime())) return '';

      const now = new Date();
      const diff = now - date;

      if (diff < 60000) { // 1分钟内
        return '刚刚';
      } else if (diff < 3600000) { // 1小时内
        return Math.floor(diff / 60000) + '分钟前';
      } else if (diff < 86400000) { // 1天内
        return Math.floor(diff / 3600000) + '小时前';
      } else if (diff < 2592000000) { // 30天内
        return Math.floor(diff / 86400000) + '天前';
      } else {
        return date.toLocaleDateString();
      }
    },

    // 检查是否可以删除评论
    canDeleteComment(comment) {
      return this.isLoggedIn && comment.is_owner;
    }
  }
}
</script>

<style scoped>
.comic-detail {
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: var(--bg);
  overflow-x: hidden;
}

/* 背景模糊效果 */
.background-blur {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: blur(20px) brightness(0.3);
  z-index: -1;
}

/* 内容包装器 */
.content-wrapper {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* 加载状态 */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--bg);
}

.loading-container {
  text-align: center;
  padding: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid var(--brand);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: #ffffff;
  font-size: 18px;
  margin: 0;
}

/* 错误状态 */
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--bg);
}

.error-container {
  text-align: center;
  padding: 60px 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 400px;
}

.error-icon {
  width: 60px;
  height: 60px;
  margin-bottom: 20px;
  color: #ff6b6b;
}

.error-container h3 {
  color: #ff6b6b;
  font-size: 28px;
  margin-bottom: 16px;
}

.error-container p {
  color: #ffffff;
  font-size: 16px;
  margin-bottom: 30px;
  line-height: 1.5;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.3);
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 172, 254, 0.4);
}

.retry-icon {
  width: 18px;
  height: 18px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
}

/* 漫画头部卡片 */
.comic-header-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 40px;
  margin-bottom: 30px;
  display: flex;
  gap: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* 封面容器 */
.cover-container {
  position: relative;
  flex-shrink: 0;
}

.cover {
  width: 240px;
  height: 320px;
  object-fit: cover;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  transition: transform 0.3s ease;
}

.cover:hover {
  transform: scale(1.02);
}

.cover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.cover-container:hover .cover-overlay {
  opacity: 1;
}

.quick-read-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.quick-read-btn:hover {
  transform: scale(1.05);
}

.quick-read-btn:disabled {
  background: #666;
  cursor: not-allowed;
}

.play-icon {
  font-size: 14px;
}

/* 漫画信息区域 */
.comic-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.title-section {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 20px;
}

.comic-title {
  font-size: 36px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 16px 0;
  line-height: 1.2;
}

.comic-tags {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.tag {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tag.category {
  background: var(--brand);
  color: white;
}

.tag.status.ongoing {
  background: var(--success);
  color: white;
}

.tag.status.completed {
  background: var(--surface-3);
  color: var(--text);
}

.tag.price.free {
  background: var(--brand-soft);
  color: var(--brand);
}

.tag.price.paid {
  background: var(--brand);
  color: white;
}

.author-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.author-label {
  color: #a0a0a0;
  font-size: 14px;
  font-weight: 500;
}

.author-name {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
}

/* 统计网格 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--brand);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #a0a0a0;
  font-weight: 500;
}

/* 操作按钮区域 */
.action-section {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.primary-read-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px;
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.3);
}

.primary-read-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 172, 254, 0.4);
}

.primary-read-btn:disabled {
  background: #666;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.favorite-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.favorite-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.favorite-btn.is-favorite {
  background: rgba(248, 113, 113, 0.2);
  border-color: rgba(248, 113, 113, 0.5);
  color: #fecaca;
}

.favorite-btn.is-favorite:hover:not(:disabled) {
  background: rgba(248, 113, 113, 0.3);
}

.favorite-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.secondary-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.secondary-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.btn-icon {
  width: 16px;
  height: 16px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
}

/* 卡片通用样式 */
.intro-card,
.chapters-card,
.copyright-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.card-header {
  padding: 30px 40px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
}

.title-icon {
  width: 28px;
  height: 28px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
  color: var(--brand);
}

.chapter-count {
  font-size: 16px;
  color: var(--brand);
  font-weight: 500;
}

.card-content {
  padding: 30px 40px;
}

/* 简介卡片 */
.description {
  color: #e0e0e0;
  font-size: 18px;
  line-height: 1.8;
  margin: 0;
}

/* 版权信息样式 */
.copyright-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}
.copyright-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;
}
.copyright-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}
.copyright-label {
  font-size: 12px;
  color: #a0a0a0;
  font-weight: 500;
  letter-spacing: 1px;
}
.copyright-value {
  font-size: 16px;
  color: #ffffff;
  font-weight: 600;
}
.copyright-statement {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 18px;
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.2);
  border-radius: 12px;
  font-size: 13px;
  color: #ffcc80;
  line-height: 1.6;
}
.statement-icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* 章节控制按钮 */
.chapter-controls {
  display: flex;
  gap: 12px;
}

.sort-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.sort-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.sort-icon {
  font-size: 16px;
}

/* 空章节状态 */
.empty-chapters {
  text-align: center;
  padding: 60px 20px;
  color: #a0a0a0;
}

.empty-icon {
  width: 60px;
  height: 60px;
  margin-bottom: 20px;
  opacity: 0.5;
}

.empty-chapters p {
  font-size: 18px;
  margin: 0;
}

/* 章节网格 */
.chapters-grid {
  display: grid;
  gap: 16px;
}

.chapter-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.chapter-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(79, 172, 254, 0.5);
  transform: translateX(8px);
}

.chapter-number {
  width: 40px;
  height: 40px;
  background: var(--brand);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.chapter-info {
  flex: 1;
}

.chapter-title {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.chapter-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chapter-price {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.chapter-price.free {
  background: var(--brand-soft);
  color: var(--brand);
}

.chapter-price.paid {
  background: var(--brand);
  color: white;
}

.chapter-arrow {
  color: var(--brand);
  font-size: 20px;
  font-weight: bold;
  transition: transform 0.3s ease;
}

.chapter-item:hover .chapter-arrow {
  transform: translateX(4px);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .content-wrapper {
    padding: 20px 15px;
  }

  .comic-header-card {
    flex-direction: column;
    gap: 30px;
    padding: 30px 20px;
  }

  .cover {
    width: 200px;
    height: 280px;
    margin: 0 auto;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .action-section {
    justify-content: center;
  }

  .card-header,
  .card-content {
    padding: 20px;
  }

  .comic-title {
    font-size: 28px;
  }

  .copyright-grid {
    grid-template-columns: repeat(1, 1fr);
  }
}

/* ========== 评论区域样式 ========== */

.comments-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  margin-top: 30px;
}

.comment-count {
  color: var(--text-dim);
  font-size: 14px;
  font-weight: normal;
}

.comment-stats {
  display: flex;
  align-items: center;
  gap: 15px;
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stars {
  display: flex;
  gap: 2px;
}

.star {
  color: #ddd;
  width: 16px;
  height: 16px;
  transition: color 0.2s;
}

.star.filled {
  color: var(--brand);
}

.rating-text {
  font-size: 14px;
  color: var(--text-dim);
  font-weight: 500;
}

/* 评论表单区域 */
.comment-form-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border);
}

.login-prompt {
  text-align: center;
  padding: 20px;
  color: var(--text-dim);
}

.login-prompt a {
  color: var(--brand);
  text-decoration: none;
  font-weight: 500;
}

.login-prompt a:hover {
  text-decoration: underline;
}

.add-comment-btn {
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-comment-btn:hover {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.btn-icon {
  width: 18px;
  height: 18px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
}

/* 评论表单 */
.comment-form {
  background: var(--surface-2);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border);
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.form-header h4 {
  margin: 0;
  color: var(--text);
  font-size: 18px;
}

.form-close-btn {
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 20px;
  color: var(--text-dim);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.form-close-btn:hover {
  background: var(--surface);
  color: var(--text);
  border-color: var(--border-strong);
}

.rating-input {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.rating-input label {
  font-size: 14px;
  color: var(--text-dim);
  font-weight: 500;
}

.star-rating {
  display: flex;
  gap: 4px;
}

.star-input {
  color: #ddd;
  width: 20px;
  height: 20px;
  cursor: pointer;
  transition: color 0.2s;
}

.star-input:hover,
.star-input.active {
  color: var(--brand);
}

.comment-textarea {
  width: 100%;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
}

.comment-textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 2px var(--brand-soft);
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.char-count {
  font-size: 12px;
  color: var(--text-faint);
}

.form-buttons {
  display: flex;
  gap: 10px;
}

.submit-btn {
  background: var(--brand);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: var(--brand-hover);
  transform: translateY(-1px);
}

.submit-btn:disabled {
  background: var(--surface-3);
  color: var(--text-faint);
  cursor: not-allowed;
  transform: none;
}

.cancel-btn {
  background: rgba(255, 255, 255, 0.12);
  color: var(--text);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 64px;
  white-space: nowrap;
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.35);
}

/* 评论列表 */
.comments-list {
  margin-top: 20px;
}

.empty-comments {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-dim);
}

.empty-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.comment-item {
  background: var(--surface-2);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid var(--border);
  transition: all 0.3s ease;
}

.comment-item:hover {
  border-color: var(--border-strong);
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.username {
  font-weight: 600;
  color: var(--text);
  font-size: 16px;
}

.comment-rating {
  display: flex;
  gap: 2px;
}

.comment-rating .star {
  width: 14px;
  height: 14px;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.comment-time {
  font-size: 12px;
  color: var(--text-faint);
}

.delete-btn {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid var(--danger);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  transition: all 0.2s;
  white-space: nowrap;
}

.delete-btn:hover {
  background: var(--danger);
  color: #fff;
}

.comment-content {
  margin-bottom: 12px;
  line-height: 1.6;
}

.comment-content p {
  margin: 0;
  color: var(--text);
  font-size: 14px;
}

.comment-actions {
  display: flex;
  gap: 16px;
}

.like-btn,
.reply-btn {
  background: none;
  border: none;
  color: var(--text-dim);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
}

.like-btn:hover {
  background: var(--brand-soft);
  color: var(--brand);
}

.reply-btn:hover {
  background: var(--surface-3);
  color: var(--text);
}

.like-icon,
.reply-icon {
  width: 14px;
  height: 14px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 4px;
}

.like-count {
  font-size: 12px;
  min-width: 16px;
  text-align: center;
}

/* 回复表单 */
.reply-form {
  margin-top: 16px;
  padding: 16px;
  background: var(--surface);
  border-radius: 8px;
  border: 1px solid var(--border);
}

.reply-textarea {
  width: 100%;
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.reply-textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 2px var(--brand-soft);
}

.reply-form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.reply-buttons {
  display: flex;
  gap: 8px;
}

.reply-buttons .submit-btn,
.reply-buttons .cancel-btn {
  padding: 6px 12px;
  font-size: 12px;
}

/* 回复列表 */
.replies-list {
  margin-top: 16px;
  padding-left: 20px;
  border-left: 3px solid var(--border);
}

.reply-item {
  background: var(--surface);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid var(--border);
}

.reply-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.reply-username {
  font-weight: 500;
  color: var(--brand);
  font-size: 14px;
}

.reply-time {
  font-size: 11px;
  color: var(--text-faint);
}

.reply-content p {
  margin: 0;
  color: var(--text-dim);
  font-size: 13px;
  line-height: 1.5;
}

/* 响应式设计 - 评论区域 */
@media (max-width: 768px) {
  .comment-form {
    padding: 16px;
  }

  .comment-item {
    padding: 16px;
  }

  .comment-header {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .user-info {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }

  .comment-actions {
    gap: 12px;
  }

  .replies-list {
    padding-left: 12px;
  }

  .reply-item {
    padding: 10px;
  }
}
</style>